import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCw } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar,
} from "recharts";

type PageView = {
  path: string;
  referrer: string | null;
  session_id: string;
  created_at: string;
  user_agent: string | null;
  language: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
};

const DAY = 86400000;
const fmtDate = (d: Date) => d.toISOString().slice(0, 10);
const hostOf = (u: string | null) => {
  if (!u) return "(direct)";
  try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return "(direct)"; }
};
const deviceOf = (ua: string | null) => {
  if (!ua) return "Unknown";
  const s = ua.toLowerCase();
  if (/ipad|tablet/.test(s)) return "Tablet";
  if (/mobi|android|iphone/.test(s)) return "Mobile";
  return "Desktop";
};
const browserOf = (ua: string | null) => {
  if (!ua) return "Unknown";
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return "Chrome";
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return "Safari";
  if (/Firefox\//.test(ua)) return "Firefox";
  return "Other";
};
const topN = (rec: Record<string, number>, n = 10) =>
  Object.entries(rec).sort((a, b) => b[1] - a[1]).slice(0, n);
const fmtPct = (n: number) => `${n.toFixed(1)}%`;
const fmtDuration = (ms: number) => {
  if (!ms) return "0s";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
};

import OgPreviewPanel from "./OgPreviewPanel";

export const SeoTrafficTab = () => (
  <Tabs defaultValue="overview" className="space-y-6">
    <TabsList>
      <TabsTrigger value="overview">Overview</TabsTrigger>
      <TabsTrigger value="pages">Pages</TabsTrigger>
      <TabsTrigger value="sources">Sources & Campaigns</TabsTrigger>
      <TabsTrigger value="audience">Audience</TabsTrigger>
      <TabsTrigger value="keywords">Keyword Targets</TabsTrigger>
      <TabsTrigger value="og">Social / OG</TabsTrigger>
    </TabsList>
    <TabsContent value="overview"><OverviewPanel /></TabsContent>
    <TabsContent value="pages"><PagesPanel /></TabsContent>
    <TabsContent value="sources"><SourcesPanel /></TabsContent>
    <TabsContent value="audience"><AudiencePanel /></TabsContent>
    <TabsContent value="keywords"><KeywordTargetsPanel /></TabsContent>
    <TabsContent value="og"><OgPreviewPanel /></TabsContent>
  </Tabs>
);

const useAnalytics = () => {
  const [rows, setRows] = useState<PageView[] | null>(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    const since = new Date(Date.now() - 30 * DAY).toISOString();
    const all: PageView[] = [];
    for (let from = 0; from < 5000; from += 1000) {
      const { data, error } = await supabase
        .from("page_views")
        .select("path,referrer,session_id,created_at,user_agent,language,utm_source,utm_medium,utm_campaign")
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .range(from, from + 999);
      if (error || !data || data.length === 0) break;
      all.push(...(data as PageView[]));
      if (data.length < 1000) break;
    }
    setRows(all);
    setLoading(false);
  };
  useEffect(() => { void load(); }, []);
  return { rows: rows ?? [], loading, refresh: load };
};

const computeWindow = (rows: PageView[], windowMs: number) => {
  const cutoff = Date.now() - windowMs;
  const filtered = rows.filter((r) => new Date(r.created_at).getTime() >= cutoff);
  const bySession = new Map<string, PageView[]>();
  filtered.forEach((r) => {
    const arr = bySession.get(r.session_id) ?? [];
    arr.push(r);
    bySession.set(r.session_id, arr);
  });
  const sessions = bySession.size;
  const views = filtered.length;
  let bounces = 0, totalDuration = 0, durationSessions = 0;
  bySession.forEach((evts) => {
    if (evts.length === 1) bounces++;
    else {
      const ts = evts.map((e) => new Date(e.created_at).getTime()).sort((a, b) => a - b);
      totalDuration += ts[ts.length - 1] - ts[0];
      durationSessions++;
    }
  });
  return {
    views, sessions,
    bounceRate: sessions === 0 ? 0 : (bounces / sessions) * 100,
    avgSessionMs: durationSessions === 0 ? 0 : totalDuration / durationSessions,
    pagesPerSession: sessions === 0 ? 0 : views / sessions,
  };
};

const Kpi = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <Card className="p-5">
    <p className="text-[10px] uppercase tracking-widest text-foreground/50">{label}</p>
    <p className="text-3xl font-bold mt-2">{value}</p>
    {sub && <p className="text-xs text-foreground/50 mt-1">{sub}</p>}
  </Card>
);

const OverviewPanel = () => {
  const { rows, loading, refresh } = useAnalytics();
  const w24 = useMemo(() => computeWindow(rows, DAY), [rows]);
  const w7 = useMemo(() => computeWindow(rows, 7 * DAY), [rows]);
  const w30 = useMemo(() => computeWindow(rows, 30 * DAY), [rows]);

  const daily = useMemo(() => {
    const map: Record<string, { date: string; views: number; sessions: Set<string> }> = {};
    for (let i = 29; i >= 0; i--) {
      const k = fmtDate(new Date(Date.now() - i * DAY));
      map[k] = { date: k, views: 0, sessions: new Set() };
    }
    rows.forEach((r) => {
      const k = r.created_at.slice(0, 10);
      if (map[k]) { map[k].views++; map[k].sessions.add(r.session_id); }
    });
    return Object.values(map).map((d) => ({
      date: d.date.slice(5), views: d.views, sessions: d.sessions.size,
    }));
  }, [rows]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground/60">Live first-party analytics. Last 30 days.</p>
        <Button size="sm" variant="outline" onClick={refresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      {([
        ["Last 24 hours", w24], ["Last 7 days", w7], ["Last 30 days", w30],
      ] as const).map(([title, w]) => (
        <section key={title}>
          <h3 className="text-lg font-semibold mb-3">{title}</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label="Views" value={String(w.views)} />
            <Kpi label="Sessions" value={String(w.sessions)} />
            <Kpi label="Bounce rate" value={fmtPct(w.bounceRate)} />
            <Kpi label="Pages / session" value={w.pagesPerSession.toFixed(2)} />
            <Kpi label="Avg session" value={fmtDuration(w.avgSessionMs)} />
          </div>
        </section>
      ))}
      <Card className="p-4">
        <h4 className="text-sm font-medium mb-3">Daily traffic, last 30 days</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--foreground) / 0.5)" fontSize={11} />
              <YAxis stroke="hsl(var(--foreground) / 0.5)" fontSize={11} />
              <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
              <Line type="monotone" dataKey="views" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="sessions" stroke="hsl(var(--foreground) / 0.6)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
};

const PagesPanel = () => {
  const { rows, loading, refresh } = useAnalytics();
  const data = useMemo(() => {
    const map = new Map<string, { views: number; sessions: Set<string>; bounces: number }>();
    const sessionPages = new Map<string, Set<string>>();
    rows.forEach((r) => {
      const sp = sessionPages.get(r.session_id) ?? new Set();
      sp.add(r.path);
      sessionPages.set(r.session_id, sp);
    });
    rows.forEach((r) => {
      const e = map.get(r.path) ?? { views: 0, sessions: new Set(), bounces: 0 };
      e.views++; e.sessions.add(r.session_id);
      map.set(r.path, e);
    });
    const sessionCount = new Map<string, number>();
    rows.forEach((r) => sessionCount.set(r.session_id, (sessionCount.get(r.session_id) ?? 0) + 1));
    map.forEach((e) => {
      let b = 0;
      e.sessions.forEach((sid) => {
        if ((sessionCount.get(sid) ?? 0) === 1 && (sessionPages.get(sid)?.size ?? 0) === 1) b++;
      });
      e.bounces = b;
    });
    return [...map.entries()]
      .map(([path, e]) => ({
        path, views: e.views, sessions: e.sessions.size,
        bounce: e.sessions.size ? (e.bounces / e.sessions.size) * 100 : 0,
      }))
      .sort((a, b) => b.views - a.views).slice(0, 25);
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground/60">Top 25 pages, last 30 days.</p>
        <Button size="sm" variant="outline" onClick={refresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      <Card className="p-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-foreground/50 text-xs uppercase tracking-wider">
            <tr><th className="text-left py-2">Path</th><th className="text-right">Views</th><th className="text-right">Sessions</th><th className="text-right">Bounce</th></tr>
          </thead>
          <tbody>
            {data.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-foreground/50">No data yet.</td></tr>}
            {data.map((r) => (
              <tr key={r.path} className="border-t border-border/20">
                <td className="py-2 truncate max-w-md">{r.path}</td>
                <td className="text-right text-primary">{r.views}</td>
                <td className="text-right">{r.sessions}</td>
                <td className="text-right">{fmtPct(r.bounce)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

const ListCard = ({ title, items, valueLabel = "visits" }: { title: string; items: [string, number][]; valueLabel?: string }) => (
  <Card className="p-4">
    <h4 className="text-sm font-medium mb-3">{title}</h4>
    {items.length === 0 && <p className="text-foreground/50 text-xs">No data.</p>}
    {items.map(([k, v]) => (
      <div key={k} className="flex justify-between text-sm py-1.5 border-b border-border/20 last:border-0">
        <span className="truncate mr-2">{k}</span>
        <span className="text-primary whitespace-nowrap">{v} {valueLabel}</span>
      </div>
    ))}
  </Card>
);

const SourcesPanel = () => {
  const { rows, loading, refresh } = useAnalytics();
  const { referrers, sources, mediums, campaigns } = useMemo(() => {
    const refs: Record<string, number> = {};
    const src: Record<string, number> = {};
    const med: Record<string, number> = {};
    const cmp: Record<string, number> = {};
    rows.forEach((r) => {
      refs[hostOf(r.referrer)] = (refs[hostOf(r.referrer)] || 0) + 1;
      if (r.utm_source) src[r.utm_source] = (src[r.utm_source] || 0) + 1;
      if (r.utm_medium) med[r.utm_medium] = (med[r.utm_medium] || 0) + 1;
      if (r.utm_campaign) cmp[r.utm_campaign] = (cmp[r.utm_campaign] || 0) + 1;
    });
    return { referrers: topN(refs, 15), sources: topN(src, 10), mediums: topN(med, 10), campaigns: topN(cmp, 10) };
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground/60">Where visitors come from, last 30 days.</p>
        <Button size="sm" variant="outline" onClick={refresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <ListCard title="Top referrers" items={referrers} />
        <ListCard title="UTM source" items={sources} />
        <ListCard title="UTM medium" items={mediums} />
        <ListCard title="UTM campaign" items={campaigns} />
      </div>
    </div>
  );
};

const AudiencePanel = () => {
  const { rows, loading, refresh } = useAnalytics();
  const { devices, browsers, languages } = useMemo(() => {
    const d: Record<string, number> = {};
    const b: Record<string, number> = {};
    const l: Record<string, number> = {};
    rows.forEach((r) => {
      d[deviceOf(r.user_agent)] = (d[deviceOf(r.user_agent)] || 0) + 1;
      b[browserOf(r.user_agent)] = (b[browserOf(r.user_agent)] || 0) + 1;
      const lang = (r.language ?? "unknown").slice(0, 8);
      l[lang] = (l[lang] || 0) + 1;
    });
    return { devices: topN(d, 5), browsers: topN(b, 6), languages: topN(l, 10) };
  }, [rows]);
  const deviceChart = devices.map(([name, value]) => ({ name, value }));
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground/60">Visitor breakdown, last 30 days.</p>
        <Button size="sm" variant="outline" onClick={refresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-4">
          <h4 className="text-sm font-medium mb-3">Devices</h4>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={deviceChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--foreground) / 0.5)" fontSize={11} />
                <YAxis stroke="hsl(var(--foreground) / 0.5)" fontSize={11} />
                <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Bar dataKey="value" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <ListCard title="Browsers" items={browsers} />
        <ListCard title="Languages" items={languages} />
      </div>
    </div>
  );
};

const KeywordTargetsPanel = () => {
  const [items, setItems] = useState<any[]>([]);
  const [kw, setKw] = useState({ keyword: "", language: "en", priority: 3, target_url: "", notes: "" });
  const load = async () => {
    const { data } = await supabase
      .from("seo_keyword_targets" as any)
      .select("*")
      .order("priority")
      .order("created_at", { ascending: false });
    setItems((data as any[]) ?? []);
  };
  useEffect(() => { void load(); }, []);
  const add = async () => {
    if (!kw.keyword.trim()) return;
    const { error } = await supabase.from("seo_keyword_targets" as any).insert(kw);
    if (error) return toast.error(error.message);
    setKw({ keyword: "", language: "en", priority: 3, target_url: "", notes: "" });
    load();
  };
  const del = async (id: string) => {
    await supabase.from("seo_keyword_targets" as any).delete().eq("id", id);
    load();
  };
  return (
    <div className="space-y-4">
      <Card className="p-4">
        <h4 className="text-sm font-medium mb-3">Add keyword target</h4>
        <div className="grid md:grid-cols-2 gap-3">
          <Input placeholder="Keyword" value={kw.keyword} onChange={(e) => setKw({ ...kw, keyword: e.target.value })} />
          <Input placeholder="Target URL" value={kw.target_url} onChange={(e) => setKw({ ...kw, target_url: e.target.value })} />
          <select className="bg-background border border-border/40 rounded px-3 py-2 text-sm"
            value={kw.language} onChange={(e) => setKw({ ...kw, language: e.target.value })}>
            <option value="en">English</option>
            <option value="ar">Arabic</option>
          </select>
          <Input type="number" min={1} max={5} placeholder="Priority 1-5"
            value={kw.priority}
            onChange={(e) => setKw({ ...kw, priority: parseInt(e.target.value) || 3 })} />
          <Textarea className="md:col-span-2" rows={2} placeholder="Notes"
            value={kw.notes} onChange={(e) => setKw({ ...kw, notes: e.target.value })} />
        </div>
        <Button onClick={add} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </Card>
      <div className="space-y-2">
        {items.length === 0 && <p className="text-foreground/50 text-sm">No keywords yet.</p>}
        {items.map((k) => (
          <div key={k.id} className="flex items-center justify-between p-3 border border-border/40 rounded">
            <div className="text-sm flex items-center gap-3 flex-wrap">
              <span className="font-medium">{k.keyword}</span>
              <Badge variant="outline" className="text-[10px]">{k.language}</Badge>
              <Badge variant="outline" className="text-[10px]">P{k.priority}</Badge>
              {k.target_url && (
                <a href={k.target_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                  → {k.target_url}
                </a>
              )}
              {k.notes && <span className="text-foreground/50 italic">, {k.notes}</span>}
            </div>
            <Button size="sm" variant="ghost" onClick={() => del(k.id)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SeoTrafficTab;
